/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AnomalyList.tsx
 *     Description     :   Displays detected infrastructure anomalies with severity badges
 *     Author          :   Peter Dobler
 *     Created         :   2025-01-21
 *     Modified        :   2025-01-21 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Production Integration Edition)
 *     Feature         :   021-infrastructure-data-collection (T043)
 * =========================================================================================
 */

import React, { useState, useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Database,
  PlayCircle,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge, Alert, Select, AppTooltip } from '../../components/ui';
import { DualScrollTable, usePagination, PaginationControls } from '../../components/common/TableControls';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Types
// ============================================================================

export type ChangeType = 'storage_growth' | 'pipeline_failure' | 'deleted_item';
export type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
import { CAPACITY_TYPE_OPTIONS, type CapacityType } from '../../services/snapshotsApi';
export type { CapacityType };

export interface Anomaly {
  id: string;
  change_type: ChangeType;
  severity: Severity;
  item_id: string;
  item_name: string;
  item_type: string;
  capacity_id: string | null;
  capacity_name: string | null;
  capacity_type: CapacityType | null;
  workspace_id: string | null;
  workspace_name: string | null;
  previous_value?: number | string;
  current_value?: number | string;
  change_percent?: number;
  message: string;
  blast_radius?: string;
  detected_at: string;
}

export interface AnomalyListProps {
  anomalies: Anomaly[];
  loading?: boolean;
  onRefresh?: () => void;
  defaultSeverityFilter?: Severity | 'all';
  defaultCapacityTypeFilter?: CapacityType | 'all';
  defaultChangeTypeFilter?: ChangeType | 'all';
}

// ============================================================================
// Helper Components
// ============================================================================

interface SeverityChipProps {
  severity: Severity;
}

const SeverityChip: React.FC<SeverityChipProps> = ({ severity }) => {
  switch (severity) {
    case 'CRITICAL':
      return (
        <Badge variant="error" leftIcon={<AlertCircle className="h-4 w-4" />}>
          CRITICAL
        </Badge>
      );
    case 'HIGH':
      return (
        <Badge variant="warning" leftIcon={<AlertTriangle className="h-4 w-4" />}>
          HIGH
        </Badge>
      );
    case 'MEDIUM':
      return (
        <Badge variant="info" leftIcon={<Info className="h-4 w-4" />}>
          MEDIUM
        </Badge>
      );
    case 'LOW':
      return (
        <Badge variant="default" isOutlined>
          LOW
        </Badge>
      );
    default:
      return <Badge variant="default">{severity}</Badge>;
  }
};

const ChangeTypeIcon: React.FC<{ changeType: ChangeType }> = ({ changeType }) => {
  switch (changeType) {
    case 'storage_growth':
      return <Database className="h-4 w-4 text-[var(--color-accent)]" />;
    case 'pipeline_failure':
      return <PlayCircle className="h-4 w-4 text-[var(--color-status-danger)]" />;
    case 'deleted_item':
      return <Trash2 className="h-4 w-4 text-[var(--color-text-secondary)]" />;
    default:
      return null;
  }
};

const formatChangeType = (changeType: ChangeType): string => {
  switch (changeType) {
    case 'storage_growth':
      return 'Storage Growth';
    case 'pipeline_failure':
      return 'Pipeline Failure';
    case 'deleted_item':
      return 'Deleted Item';
    default:
      return changeType;
  }
};

// ============================================================================
// Anomaly Row Component
// ============================================================================

interface AnomalyRowProps {
  anomaly: Anomaly;
}

const AnomalyRow: React.FC<AnomalyRowProps> = ({ anomaly }) => {
  const [expanded, setExpanded] = useState(false);

  const tdBase = 'px-3 py-2 align-top text-sm';
  const tdBorder = expanded ? '' : 'border-b border-[var(--color-border)]';

  return (
    <>
      <tr
        className={
          anomaly.severity === 'CRITICAL'
            ? 'bg-[var(--color-status-danger)]/5 hover:bg-[var(--color-bg-subtle)]'
            : 'hover:bg-[var(--color-bg-subtle)]'
        }
      >
        <td className={`${tdBase} ${tdBorder} w-8`}>
          <button
            type="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center justify-center rounded-[var(--radius-md)] p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          <div className="flex items-center gap-1.5">
            <ChangeTypeIcon changeType={anomaly.change_type} />
            <span className="text-[var(--color-text-primary)]">
              {formatChangeType(anomaly.change_type)}
            </span>
          </div>
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          <SeverityChip severity={anomaly.severity} />
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          <div className="font-medium text-[var(--color-text-primary)]">
            {anomaly.item_name}
          </div>
          <div className="text-xs text-[var(--color-text-secondary)]">
            {anomaly.item_type}
          </div>
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          {anomaly.capacity_name && (
            <>
              <div className="text-[var(--color-text-primary)]">{anomaly.capacity_name}</div>
              {anomaly.capacity_type && (
                <Badge variant="default" isOutlined className="mt-1">
                  {anomaly.capacity_type}
                </Badge>
              )}
            </>
          )}
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          {anomaly.change_percent !== undefined && (
            <span
              className={
                anomaly.change_percent > 50
                  ? 'font-medium text-[var(--color-status-danger)]'
                  : anomaly.change_percent > 20
                  ? 'font-medium text-[var(--color-status-warning)]'
                  : 'font-medium text-[var(--color-text-primary)]'
              }
            >
              +{anomaly.change_percent.toFixed(1)}%
            </span>
          )}
        </td>
        <td className={`${tdBase} ${tdBorder}`}>
          <AppTooltip title={formatEastern(anomaly.detected_at)}>
            <span className="text-xs text-[var(--color-text-secondary)]">
              {formatDistanceToNow(new Date(anomaly.detected_at), { addSuffix: true })}
            </span>
          </AppTooltip>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="border-b border-[var(--color-border)] p-0">
            <div className="bg-[var(--color-bg-subtle)] px-4 py-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <div className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">
                    Message
                  </div>
                  <div className="text-sm text-[var(--color-text-primary)]">{anomaly.message}</div>
                </div>
                {anomaly.blast_radius && (
                  <div className="col-span-2">
                    <Alert
                      variant={
                        anomaly.severity === 'CRITICAL'
                          ? 'error'
                          : anomaly.severity === 'HIGH'
                          ? 'warning'
                          : 'info'
                      }
                      className="mt-1"
                    >
                      <span className="font-medium">Blast Radius:</span>
                      {' '}
                      {anomaly.blast_radius}
                    </Alert>
                  </div>
                )}
                <div>
                  <div className="text-xs text-[var(--color-text-secondary)]">Workspace</div>
                  <div className="text-sm text-[var(--color-text-primary)]">
                    {anomaly.workspace_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[var(--color-text-secondary)]">Item ID</div>
                  <div className="font-mono text-xs text-[var(--color-text-primary)]">
                    {anomaly.item_id}
                  </div>
                </div>
                {anomaly.previous_value !== undefined && anomaly.current_value !== undefined && (
                  <>
                    <div>
                      <div className="text-xs text-[var(--color-text-secondary)]">Previous Value</div>
                      <div className="text-sm text-[var(--color-text-primary)]">{String(anomaly.previous_value)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[var(--color-text-secondary)]">Current Value</div>
                      <div className="text-sm text-[var(--color-text-primary)]">{String(anomaly.current_value)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const AnomalyList: React.FC<AnomalyListProps> = ({
  anomalies,
  loading = false,
  onRefresh,
  defaultSeverityFilter = 'all',
  defaultCapacityTypeFilter = 'all',
  defaultChangeTypeFilter = 'all',
}) => {
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>(defaultSeverityFilter);
  const [capacityTypeFilter, setCapacityTypeFilter] = useState<CapacityType | 'all'>(
    defaultCapacityTypeFilter
  );
  const [changeTypeFilter, setChangeTypeFilter] = useState<ChangeType | 'all'>(
    defaultChangeTypeFilter
  );

  // Filter anomalies
  const filteredAnomalies = useMemo(() => {
    return anomalies.filter((anomaly) => {
      if (severityFilter !== 'all' && anomaly.severity !== severityFilter) {
        return false;
      }
      if (capacityTypeFilter !== 'all' && anomaly.capacity_type !== capacityTypeFilter) {
        return false;
      }
      if (changeTypeFilter !== 'all' && anomaly.change_type !== changeTypeFilter) {
        return false;
      }
      return true;
    });
  }, [anomalies, severityFilter, capacityTypeFilter, changeTypeFilter]);

  // Calculate counts
  const counts = useMemo(() => {
    return {
      critical: anomalies.filter((a) => a.severity === 'CRITICAL').length,
      high: anomalies.filter((a) => a.severity === 'HIGH').length,
      medium: anomalies.filter((a) => a.severity === 'MEDIUM').length,
      total: anomalies.length,
    };
  }, [anomalies]);

  // Pagination over the filtered anomalies
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(filteredAnomalies);

  if (loading) {
    return (
      <div>
        <div className="mb-4 h-12 animate-pulse rounded bg-[var(--color-bg-subtle)]" />
        <div className="h-[200px] animate-pulse rounded bg-[var(--color-bg-subtle)]" />
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-base font-bold text-[var(--color-text-primary)]">
          Detected Anomalies
        </span>
        {counts.critical > 0 && (
          <Badge variant="error">{`${counts.critical} Critical`}</Badge>
        )}
        {counts.high > 0 && (
          <Badge variant="warning">{`${counts.high} High`}</Badge>
        )}
        {counts.medium > 0 && (
          <Badge variant="info">{`${counts.medium} Medium`}</Badge>
        )}
        <div className="flex-grow" />
        {onRefresh && (
          <AppTooltip title="Refresh">
            <button
              type="button"
              aria-label="Refresh"
              onClick={onRefresh}
              className="inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </AppTooltip>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select
            isFullWidth
            selectSize="sm"
            label="Severity"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as Severity | 'all')}
            options={[
              { value: 'all', label: 'All Severities' },
              { value: 'CRITICAL', label: 'Critical' },
              { value: 'HIGH', label: 'High' },
              { value: 'MEDIUM', label: 'Medium' },
              { value: 'LOW', label: 'Low' },
            ]}
          />
          <Select
            isFullWidth
            selectSize="sm"
            label="Capacity Type"
            value={capacityTypeFilter}
            onChange={(e) => setCapacityTypeFilter(e.target.value as CapacityType | 'all')}
            options={[{ value: 'all', label: 'All Types' }, ...CAPACITY_TYPE_OPTIONS]}
          />
          <Select
            isFullWidth
            selectSize="sm"
            label="Change Type"
            value={changeTypeFilter}
            onChange={(e) => setChangeTypeFilter(e.target.value as ChangeType | 'all')}
            options={[
              { value: 'all', label: 'All Changes' },
              { value: 'storage_growth', label: 'Storage Growth' },
              { value: 'pipeline_failure', label: 'Pipeline Failure' },
              { value: 'deleted_item', label: 'Deleted Item' },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      {filteredAnomalies.length === 0 ? (
        <Alert variant="success" className="mt-2">
          No anomalies detected matching the current filters.
        </Alert>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)]">
          <DualScrollTable>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[var(--color-bg-subtle)] text-left">
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]" />
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Type</th>
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Severity</th>
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Item</th>
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Capacity</th>
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Change</th>
                  <th className="px-3 py-2 font-medium text-[var(--color-text-secondary)]">Detected</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((anomaly) => (
                  <AnomalyRow key={anomaly.id} anomaly={anomaly} />
                ))}
              </tbody>
            </table>
          </DualScrollTable>
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            start={start}
            shown={pageItems.length}
            onPage={setPage}
            onPageSize={changeSize}
          />
        </div>
      )}
    </div>
  );
};

export default AnomalyList;
